import React from 'react';
import Box from '@mui/material/Box';
import FormLabel from '@mui/material/FormLabel';
import FormControl from '@mui/material/FormControl';
import FormGroup from '@mui/material/FormGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormHelperText from '@mui/material/FormHelperText';
import Checkbox from '@mui/material/Checkbox';
import { alpha } from '@mui/material/styles';
import { useField } from 'formik';

interface CheckboxOption {
    value: string;
    label: string;
    disabled?: boolean;
}

interface ComboCheckboxProps {
    name: string;
    label?: string;
    options: CheckboxOption[];
    helperText?: string;
    required?: boolean;
    disabled?: boolean;
    sx?: any;
    onChange?: (selectedValues: string[]) => void;
}

export const ComboCheckbox = ({
    name,
    label,
    options,
    helperText,
    required = false,
    disabled = false,
    sx,
    onChange,
}: ComboCheckboxProps) => {
    const [field, meta, helpers] = useField(name);
    const { value = [] } = field;
    const { error, touched } = meta;
    const { setValue } = helpers;

    const handleChange = (optionValue: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
        const isChecked = event.target.checked;
        let newValue: string[];

        if (isChecked) {
            newValue = [...value, optionValue];
        } else {
            newValue = value.filter((val: string) => val !== optionValue);
        }

        setValue(newValue);
        onChange?.(newValue);
    };

    const isChecked = (optionValue: string) => value.includes(optionValue);

    return (
        <Box sx={{ width: '100%', ...sx }}>
            <FormControl
                component="fieldset"
                variant="standard"
                error={touched && !!error}
                disabled={disabled}
                required={required}
                sx={{ width: '100%' }}
            >
                {label && (
                    <FormLabel
                        component="legend"
                        sx={{
                            fontWeight: 500,
                            lineHeight: '22px',
                            marginBottom: 1,
                        }}
                    >
                        {label}
                    </FormLabel>
                )}
                <FormGroup sx={{ width: '100%' }}>
                    {options.map(option => {
                        const checked = isChecked(option.value);
                        return (
                            <Box
                                key={option.value}
                                sx={theme => ({
                                    'width': '100%',
                                    'backgroundColor': checked
                                        ? alpha(theme.palette.primary.main, 0.08)
                                        : theme.palette.background.default,
                                    'borderRadius': 1,
                                    'border': checked
                                        ? `1px solid ${theme.palette.primary.main}`
                                        : `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                                    'marginY': 0.3,
                                    'padding': 0,
                                    'transition': theme.transitions.create(
                                        ['background-color', 'border-color', 'box-shadow'],
                                        {
                                            duration: theme.transitions.duration.short,
                                        }
                                    ),
                                    '&:hover': {
                                        backgroundColor: checked
                                            ? alpha(theme.palette.primary.main, 0.12)
                                            : theme.palette.action.hover,
                                        borderColor: checked
                                            ? theme.palette.primary.main
                                            : theme.palette.action.disabled,
                                    },
                                    '&:focus-within': {
                                        boxShadow: `0 0 0 2px ${alpha(
                                            theme.palette.primary.main,
                                            0.2
                                        )}`,
                                        borderColor: theme.palette.primary.main,
                                    },
                                })}
                            >
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={checked}
                                            onChange={handleChange(option.value)}
                                            name={`${name}-${option.value}`}
                                            disabled={disabled || option.disabled}
                                        />
                                    }
                                    label={option.label}
                                    sx={{
                                        'margin': 0,
                                        'width': '100%',
                                        '& .MuiFormControlLabel-label': {
                                            fontWeight: 500,
                                            lineHeight: '20px',
                                            flex: 1,
                                        },
                                    }}
                                />
                            </Box>
                        );
                    })}
                </FormGroup>
                {(helperText || (touched && error)) && (
                    <FormHelperText>{touched && error ? error : helperText}</FormHelperText>
                )}
            </FormControl>
        </Box>
    );
};
